import { useState } from "react";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Shield, Coins, KeyRound, Mail, Ban, Check, Trash2, AtSign, Clock, Crown } from "lucide-react";
import { ConfirmButton, timeAgo } from "./AdminShared";
import useAvatarPeek from "@/hooks/useAvatarPeek";

const isSuspended = (u) => u.banned_until && new Date(u.banned_until) > new Date();

export default function UsersTab({
  users,
  me,
  onSearch,
  onToggleCreator,
  onToggleAdmin,
  onToggleTechAdmin,
  onGrantLifetimeBoss,
  onGrantCoins,
  onTogglePaying,
  onResetPassword,
  onSendResetEmail,
  onSetHandle,
  onBan,
  onUnban,
  onSuspend,
  onDelete,
}) {
  const [q, setQ] = useState("");
  const peek = useAvatarPeek();
  const isTechScope = me?.admin_scope === "technical";
  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch(q)}
          placeholder="Search by email / handle / name…"
          className="bg-black/40 border-white/10 rounded-none py-6 max-w-md"
          data-testid="admin-user-search"
        />
        <Button onClick={() => onSearch(q)} className="btn-crimson rounded-none">Search</Button>
      </div>
      <div className="space-y-2">
        {users.map((u) => (
          <div
            key={u.id}
            data-testid={`admin-user-${u.id}`}
            className={`card-surface p-4 flex items-center gap-4 ${u.banned ? "opacity-60" : ""}`}
          >
            <button
              type="button"
              onClick={() => peek.open(u)}
              className="shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-rose-500/60"
              data-testid={`admin-user-avatar-${u.handle}`}
              title={`View ${u.display_name}'s avatar`}
            >
              <Avatar className="w-10 h-10 border border-white/10 hover:border-rose-500 transition-colors cursor-pointer">
                <AvatarImage src={u.avatar_url} />
                <AvatarFallback className="bg-rose-600 text-white text-sm">
                  {u.handle?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => peek.open(u)}
                  data-testid={`admin-user-name-${u.handle}`}
                  className="font-medium truncate hover:text-rose-400 transition-colors text-left"
                >
                  {u.display_name || u.handle}
                </button>
                <Link
                  to={`/u/${u.id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`admin-user-link-${u.handle}`}
                  className="text-zinc-500 text-xs hover:text-rose-400 transition-colors"
                >
                  {u.display_name}
                </Link>
                {u.is_owner && <span className="text-[10px] uppercase tracking-widest text-amber-400 border border-amber-400/60 px-1.5 py-0.5">owner</span>}
                {u.role === "admin" && !u.is_owner && u.admin_scope === "technical" && <span data-testid={`admin-user-${u.id}-tech-badge`} className="text-[10px] uppercase tracking-widest text-cyan-400 border border-cyan-500/50 px-1.5 py-0.5">tech admin</span>}
                {u.role === "admin" && !u.is_owner && u.admin_scope !== "technical" && <span className="text-[10px] uppercase tracking-widest text-rose-500 border border-rose-600/50 px-1.5 py-0.5">admin</span>}
                {u.is_creator && <span className="text-[10px] uppercase tracking-widest text-amber-400 border border-amber-500/50 px-1.5 py-0.5">creator</span>}
                {u.banned && <span className="text-[10px] uppercase tracking-widest text-rose-500 border border-rose-600 px-1.5 py-0.5">banned</span>}
                {!u.banned && isSuspended(u) && <span className="text-[10px] uppercase tracking-widest text-orange-400 border border-orange-500/60 px-1.5 py-0.5">suspended</span>}
              </div>
              <div className="text-xs text-zinc-500 truncate">
                {u.email} · {u.coins} coins · joined {timeAgo(u.created_at)} ago
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <ConfirmButton
                testid={`admin-user-${u.id}-creator`}
                label={u.is_creator ? "Remove creator status?" : "Mark as creator?"}
                description="Creators can be tipped and appear with a verified badge."
                onConfirm={() => onToggleCreator(u)}
              >
                <Sparkles size={13} /> {u.is_creator ? "Un-creator" : "Creator"}
              </ConfirmButton>
              {me?.is_owner && (u.handle === "freddyovahness" || u.lifetime_boss) && (
                <ConfirmButton
                  testid={`admin-user-${u.id}-lifetime-boss`}
                  label={u.lifetime_boss ? "Lifetime Boss badge already granted" : "Grant PERMANENT Boss badge?"}
                  description="Owner-only. Separate from monthly VIP tiers, never resets, idempotent, audited. Touches no coins, wallet, Stripe, payouts, commissions or the 45/55 split."
                  onConfirm={() => onGrantLifetimeBoss(u)}
                >
                  <Crown size={13} /> {u.lifetime_boss ? "Boss ✓" : "Lifetime Boss"}
                </ConfirmButton>
              )}
              {me?.is_owner && !u.is_owner && (
                <ConfirmButton
                  testid={`admin-user-${u.id}-admin`}
                  label={u.role === "admin" ? "Demote from admin?" : "Promote to admin?"}
                  description="Admins have full moderation power. Only the owner can change roles."
                  onConfirm={() => onToggleAdmin(u)}
                >
                  <Shield size={13} /> {u.role === "admin" ? "Demote" : "Admin"}
                </ConfirmButton>
              )}
              {me?.is_owner && !u.is_owner && (
                <ConfirmButton
                  testid={`admin-user-${u.id}-tech-admin`}
                  label={u.role === "admin" && u.admin_scope === "technical" ? "Remove Technical Admin role?" : "Make Technical Admin (AI Admin)?"}
                  description="Technical Admins get full content, moderation and site-settings access, but NO financial, credential or role-change powers."
                  onConfirm={() => onToggleTechAdmin(u)}
                >
                  <Shield size={13} /> {u.role === "admin" && u.admin_scope === "technical" ? "Un-tech" : "Tech Admin"}
                </ConfirmButton>
              )}
              {!isTechScope && (
              <ConfirmButton
                testid={`admin-user-${u.id}-coins`}
                label="Grant 500 coins?"
                description="This will credit the user's wallet with 500 coins."
                onConfirm={() => onGrantCoins(u.id)}
              >
                <Coins size={13} /> +500
              </ConfirmButton>
              )}
              {!isTechScope && (
              <ConfirmButton
                testid={`admin-user-${u.id}-paying`}
                label={u.is_paying_customer ? "Remove paying-member status?" : "Mark as paying member?"}
                description={u.is_paying_customer
                  ? "They'll lose access to daily Golden Tickets."
                  : "Unlocks daily Golden Tickets. Use for users who paid via the live Payment Link."}
                onConfirm={() => onTogglePaying(u)}
              >
                <Coins size={13} /> {u.is_paying_customer ? "Un-pay" : "Paying"}
              </ConfirmButton>
              )}
              {(!u.is_owner || me?.id === u.id) && !isTechScope && (
                <ConfirmButton
                  testid={`admin-user-${u.id}-reset-pw`}
                  label={`Reset password for ${u.display_name}?`}
                  description="Generates a new temporary password and shows it once so you can share it with the user."
                  onConfirm={() => onResetPassword(u)}
                >
                  <KeyRound size={13} /> Reset PW
                </ConfirmButton>
              )}
              {(!u.is_owner || me?.id === u.id) && (
                <ConfirmButton
                  testid={`admin-user-${u.id}-email-reset`}
                  label={`Send password-reset link to ${u.email}?`}
                  description="Emails the user a 24-hour reset link. Also shows you the URL directly so you can copy it if the email doesn't reach them."
                  onConfirm={() => onSendResetEmail(u)}
                >
                  <Mail size={13} /> Email Reset
                </ConfirmButton>
              )}
              <button
                type="button"
                data-testid={`admin-user-${u.id}-rename-handle`}
                onClick={() => onSetHandle(u)}
                className="inline-flex items-center gap-1 text-xs rounded-none border border-white/15 hover:border-rose-500/60 px-3 py-1.5 text-white"
                title="Rename this user's @username"
              >
                <AtSign size={13} /> Rename
              </button>
              {u.banned ? (
                <ConfirmButton
                  testid={`admin-user-${u.id}-unban`}
                  label="Unban user?"
                  description="This user will regain full access."
                  onConfirm={() => onUnban(u.id)}
                >
                  <Check size={13} /> Unban
                </ConfirmButton>
              ) : !u.is_owner && isSuspended(u) ? (
                <ConfirmButton
                  testid={`admin-user-${u.id}-unsuspend`}
                  label="Lift suspension early?"
                  description="This user will regain access immediately."
                  onConfirm={() => onUnban(u.id)}
                >
                  <Check size={13} /> Unsuspend
                </ConfirmButton>
              ) : !u.is_owner ? (
                <>
                  <button
                    type="button"
                    data-testid={`admin-user-${u.id}-suspend`}
                    onClick={() => onSuspend(u)}
                    className="inline-flex items-center gap-1 text-xs rounded-none border border-white/15 hover:border-amber-400/60 px-3 py-1.5 text-white"
                    title="Timed suspension — auto-lifts when it expires"
                  >
                    <Clock size={13} /> Suspend
                  </button>
                  <ConfirmButton
                    testid={`admin-user-${u.id}-ban`}
                    variant="danger"
                    label="Ban user?"
                    description="They will be blocked from logging in until unbanned."
                    onConfirm={() => onBan(u.id)}
                  >
                    <Ban size={13} /> Ban
                  </ConfirmButton>
                </>
              ) : null}
              {!u.is_owner && (
                <ConfirmButton
                  testid={`admin-user-${u.id}-delete`}
                  variant="danger"
                  label="Delete user permanently?"
                  description="This removes the user and all their posts, comments, memberships. Irreversible."
                  onConfirm={() => onDelete(u.id)}
                >
                  <Trash2 size={13} /> Delete
                </ConfirmButton>
              )}
            </div>
          </div>
        ))}
        {users.length === 0 && (
          <div className="card-surface p-8 text-center text-sm text-zinc-500">No users match.</div>
        )}
      </div>
      {peek.node}
    </>
  );
}
